import { create } from "zustand";
import { persist } from "zustand/middleware";

export const ONBOARDING_STEPS = {
  CREATE_ACCOUNT: "CREATE_ACCOUNT",
  LINK_CEX: "LINK_CEX",
  CONNECT_SOCIAL: "CONNECT_SOCIAL",
} as const;

export type OnboardingStepType = (typeof ONBOARDING_STEPS)[keyof typeof ONBOARDING_STEPS];

// Export step order for use across components
export const ONBOARDING_STEP_ORDER: OnboardingStepType[] = [
  ONBOARDING_STEPS.CREATE_ACCOUNT,
  ONBOARDING_STEPS.LINK_CEX,
  ONBOARDING_STEPS.CONNECT_SOCIAL,
];

interface OnboardingState {
  // Modal state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;

  // Current step
  currentStep: OnboardingStepType;
  setCurrentStep: (step: OnboardingStepType) => void;

  // Navigation helpers
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  canGoNext: () => boolean;
  canGoPrevious: () => boolean;

  // Reset state
  reset: () => void;

  // Onboarding completion
  isOnboardingCompleted: boolean;
  setOnboardingCompleted: (completed: boolean) => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      // Modal state
      isOpen: false,
      setIsOpen: (open) => set({ isOpen: open }),

      // Current step
      currentStep: ONBOARDING_STEPS.CREATE_ACCOUNT,
      setCurrentStep: (step) => set({ currentStep: step }),

      // Navigation helpers
      goToNextStep: () => {
        const state = get();
        const currentIndex = ONBOARDING_STEP_ORDER.indexOf(state.currentStep);
        if (currentIndex < ONBOARDING_STEP_ORDER.length - 1) {
          set({ currentStep: ONBOARDING_STEP_ORDER[currentIndex + 1] });
        }
      },
      goToPreviousStep: () => {
        const state = get();
        const currentIndex = ONBOARDING_STEP_ORDER.indexOf(state.currentStep);
        if (currentIndex > 0) {
          set({ currentStep: ONBOARDING_STEP_ORDER[currentIndex - 1] });
        }
      },
      canGoNext: () => {
        const state = get();
        const currentIndex = ONBOARDING_STEP_ORDER.indexOf(state.currentStep);
        return currentIndex < ONBOARDING_STEP_ORDER.length - 1;
      },
      canGoPrevious: () => {
        const state = get();
        const currentIndex = ONBOARDING_STEP_ORDER.indexOf(state.currentStep);
        return currentIndex > 0;
      },

      // Reset state
      reset: () => {
        set({
          isOpen: false,
          currentStep: ONBOARDING_STEPS.CREATE_ACCOUNT,
          isOnboardingCompleted: false,
        });
      },

      // Onboarding completion
      isOnboardingCompleted: false,
      setOnboardingCompleted: (completed) => set({ isOnboardingCompleted: completed }),
    }),
    {
      name: "onboarding-storage",
      partialize: (state) => ({
        isOnboardingCompleted: state.isOnboardingCompleted,
      }),
    },
  ),
);
